import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { securePut, secureGet } from '../../config/api';
import Input from '../../components/Input';
import { SaveCancel } from '../../components/PrimaryButton';
import closeIcon from '../../icons/xB.svg';
import chevronDownIcon from '../../icons/chevron-down.svg';
import chevronUpIcon from '../../icons/chevron-upG.svg';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';

const STATUSES = [
  { key: 'TODO', label: 'À Faire', color: '#3872B0' },
  { key: 'ONGOING', label: 'En Cours', color: '#3872B0' },
  { key: 'PAUSED', label: 'En Pause', color: '#3872B0' },
  { key: 'BLOCKED', label: 'Bloqué', color: '#EF456E' },
  { key: 'DONE', label: 'Terminé', color: '#06D6A0' },
];



const TaskDetailPopup = ({ open, task, technician, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [fetchingTask, setFetchingTask] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [currentTaskStatus, setCurrentTaskStatus] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showAuthSection, setShowAuthSection] = useState(false);
  const [showBlockReason, setShowBlockReason] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [customBlockReason, setCustomBlockReason] = useState('');
  const [originalBlockReason, setOriginalBlockReason] = useState(null); // Store original blocked reason
  const [originalCustomBlockReason, setOriginalCustomBlockReason] = useState(null); // Store original custom reason
  const [code, setCode] = useState(['', '', '', '']);
  const [codeError, setCodeError] = useState(false);
  const dropdownRef = useRef(null);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const customReasonInputRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  const BLOCK_REASONS = [
    { value: 'MISSING_PART', name: t('blocked_reasons.MISSING_PART') },
    { value: 'LABOR_DIFFICULTY', name: t('blocked_reasons.LABOR_DIFFICULTY') },
    { value: 'TOOL_NOT_AVAILABLE', name: t('blocked_reasons.TOOL_NOT_AVAILABLE') },
    { value: 'WAITING_CLIENT_APPROVAL', name: t('blocked_reasons.WAITING_CLIENT_APPROVAL') },
    { value: 'TRAINING_TIME', name: t('blocked_reasons.TRAINING_TIME') },
    { value: 'TECHNICIAN_ABSENT', name: t('blocked_reasons.TECHNICIAN_ABSENT') },
    { value: 'EXTERNAL_URGENCY', name: t('blocked_reasons.EXTERNAL_URGENCY') },
    { value: 'OTHER', name: "Autre" },
  ];
  // Format duration from minutes (integer) to "XhYm" format
  const formatDuration = (minutes) => {
    // Handle null, undefined, or 0
    if (!minutes || minutes === 0) return '0h';
    
    // Convert to number if it's a string
    const minutesNum = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
    
    if (isNaN(minutesNum)) return '—';
    
    const wholeHours = Math.floor(minutesNum / 60);
    const mins = minutesNum % 60;
    
    if (mins === 0) {
      return `${wholeHours}h`;
    } else if (wholeHours === 0) {
      return `${mins}min`;
    } else {
      return `${wholeHours}h${mins}min`;
    }
  };

  const getElapsedEndDate = (endDate, status, statusLastChangedAt) => {
    if (endDate) return new Date(endDate);
    if ((status === 'PAUSED' || status === 'BLOCKED') && statusLastChangedAt) {
      return new Date(statusLastChangedAt);
    }
    return new Date();
  };

  const calculateElapsedTime = (startDate, endDate, totalBlockedTime = 0, totalPausedTime = 0, status, statusLastChangedAt) => {
    if (!startDate) return null; // Empty if startDate is null

    const start = new Date(startDate);
    const end = getElapsedEndDate(endDate, status, statusLastChangedAt);

    const diffMinutes = Math.max(0, Math.floor((end - start) / (1000 * 60)) - (totalBlockedTime || 0) - (totalPausedTime || 0));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (hours === 0 && minutes === 0) return '0min';
    if (hours === 0) return `${minutes}min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes}min`;
  };

  // Fetch task details by ID when popup opens
  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (open && task?.id) {
        try {
          setFetchingTask(true);
          const response = await secureGet(`/projects/tasks/${task.id}`, showSnackbar);
          console.log("task details fetched", response.data);
          setTaskData(response.data);
          
          // Update currentTaskStatus from fetched data
          if (response.data?.status) {
            setCurrentTaskStatus(response.data.status);
          }

          // Pre-fill block reason if status is BLOCKED and blockedReason exists
          if (response.data?.status === 'BLOCKED') {
            // Set pendingStatus so user can change reason and submit
            setPendingStatus(STATUSES.find(s => s.key === 'BLOCKED'));
            
            if (response.data?.blockedReason) {
              const existingReason = response.data.blockedReason;
              // Check if it matches one of the enum values
              const matchingReason = BLOCK_REASONS.find(r => r.value === existingReason);
              if (matchingReason) {
                setBlockReason(matchingReason.value);
                setOriginalBlockReason(matchingReason.value);
                setOriginalCustomBlockReason(null);
              } else {
                // It's a custom reason (OTHER)
                setBlockReason('OTHER');
                setCustomBlockReason(existingReason);
                setOriginalBlockReason('OTHER');
                setOriginalCustomBlockReason(existingReason);
              }
            }
            setShowBlockReason(true);
            // Don't show auth section initially - only show if reason changes
            setShowAuthSection(false);
          }

          // Fetch vehicle details if vehicleId exists
          if (response.data?.vehicleId) {
            try {
              const vehicleResponse = await secureGet(`/vehicles/${response.data.vehicleId}`, showSnackbar);
              console.log("vehicle details fetched", vehicleResponse.data);
              setVehicleData(vehicleResponse.data);
            } catch (error) {
              console.error('Error fetching vehicle details:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching task details:', error);
          // Fallback to prop task if fetch fails
          setTaskData(task);
          if (task?.status) {
            setCurrentTaskStatus(task.status);
          }
          // Pre-fill block reason from prop task if status is BLOCKED
          if (task?.status === 'BLOCKED') {
            // Set pendingStatus so user can change reason and submit
            setPendingStatus(STATUSES.find(s => s.key === 'BLOCKED'));
            
            if (task?.blockedReason) {
              const existingReason = task.blockedReason;
              const matchingReason = BLOCK_REASONS.find(r => r.value === existingReason);
              if (matchingReason) {
                setBlockReason(matchingReason.value);
                setOriginalBlockReason(matchingReason.value);
                setOriginalCustomBlockReason(null);
              } else {
                setBlockReason('OTHER');
                setCustomBlockReason(existingReason);
                setOriginalBlockReason('OTHER');
                setOriginalCustomBlockReason(existingReason);
              }
            }
            setShowBlockReason(true);
            // Don't show auth section initially - only show if reason changes
            setShowAuthSection(false);
          }
        } finally {
          setFetchingTask(false);
        }
      } else if (open && task) {
        // If no ID but task prop exists, use it directly
        setTaskData(task);
    if (task?.status) {
      setCurrentTaskStatus(task.status);
    }
        // Pre-fill block reason if status is BLOCKED
        if (task?.status === 'BLOCKED') {
          // Set pendingStatus so user can change reason and submit
          setPendingStatus(STATUSES.find(s => s.key === 'BLOCKED'));
          
          if (task?.blockedReason) {
            const existingReason = task.blockedReason;
            const matchingReason = BLOCK_REASONS.find(r => r.value === existingReason);
            if (matchingReason) {
              setBlockReason(matchingReason.value);
              setOriginalBlockReason(matchingReason.value);
              setOriginalCustomBlockReason(null);
            } else {
              setBlockReason('OTHER');
              setCustomBlockReason(existingReason);
              setOriginalBlockReason('OTHER');
              setOriginalCustomBlockReason(existingReason);
            }
          }
          setShowBlockReason(true);
          // Don't show auth section initially - only show if reason changes
          setShowAuthSection(false);
        }
      }
    };

    fetchTaskDetails();
  }, [open, task?.id]);

  // Reset taskData and vehicleData when popup closes
  useEffect(() => {
    if (!open) {
      setTaskData(null);
      setVehicleData(null);
      setCurrentTaskStatus(null);
      // Reset snackbar when popup closes
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Auto-focus custom reason input when "OTHER" is selected
  useEffect(() => {
    if (blockReason === 'OTHER' && customReasonInputRef.current && showBlockReason) {
      // Use setTimeout to ensure the input is rendered before focusing
      setTimeout(() => {
        const inputElement = customReasonInputRef.current?.querySelector('input');
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }, [blockReason, showBlockReason]);

  // Auto-focus first input when auth section is shown (only if block reason section is not shown)
  useEffect(() => {
    if (showAuthSection && !showBlockReason && inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, [showAuthSection, showBlockReason]);

  if (!open || !task) return null;

  // Use fetched taskData if available, otherwise fallback to prop task
  const displayTask = taskData || task;

  // Use pendingStatus if available (selected but not confirmed), otherwise use currentTaskStatus or task.status
  const displayStatus = pendingStatus?.key || currentTaskStatus || displayTask?.status;
  const currentStatus = STATUSES.find(s => s.key === displayStatus) || STATUSES[0];

  const handleStatusSelect = (status) => {
    setShowStatusDropdown(false);
    
    // Hide block reason section if switching from BLOCKED to another status
    if (showBlockReason && status.key !== 'BLOCKED') {
      setShowBlockReason(false);
      setBlockReason('');
      setCustomBlockReason('');
    }
    
    if (status.key === 'BLOCKED') {
      // Show block reason selection
      setPendingStatus(status);
      setShowBlockReason(true);
      
      // Pre-fill block reason if task already has one
      const displayTask = taskData || task;
      if (displayTask?.blockedReason) {
        const existingReason = displayTask.blockedReason;
        const matchingReason = BLOCK_REASONS.find(r => r.value === existingReason);
        if (matchingReason) {
          setBlockReason(matchingReason.value);
        } else {
          // It's a custom reason
          setBlockReason('OTHER');
          setCustomBlockReason(existingReason);
        }
      }
      
      setShowAuthSection(false); // Don't show auth section until reason is confirmed
    } else {
      // Show auth section for other status changes
      setPendingStatus(status);
      setShowAuthSection(true);
      setShowBlockReason(false); // Hide block reason section
    }
  };

  const handleBlockReasonChange = (value) => {
    setBlockReason(value);
    
    // If OTHER is selected, clear customBlockReason if it was set from a previous OTHER selection
    if (value !== 'OTHER') {
      setCustomBlockReason('');
    }
    
    // Show auth section if the reason has changed from the original
    // Once shown, keep it visible until form is submitted or cancelled
    const hasChanged = value !== originalBlockReason;
    
    if (hasChanged && value) {
      // Reason has changed from original, show auth section and keep it visible
      setShowAuthSection(true);
    } else if (!hasChanged && value === originalBlockReason) {
      // Reason matches original exactly, hide auth section
      setShowAuthSection(false);
    }
    // If auth section is already shown, keep it visible (don't hide when switching between different reasons)
  };

  const handleCustomBlockReasonChange = (value) => {
    setCustomBlockReason(value);
    
    // Show auth section if the custom reason has changed from the original
    // Once shown, keep it visible until form is submitted or cancelled
    const hasChanged = value.trim() !== (originalCustomBlockReason || '').trim();
    
    if (hasChanged && value.trim()) {
      // Custom reason has changed, show auth section and keep it visible
      setShowAuthSection(true);
    } else if (!hasChanged && blockReason === originalBlockReason && blockReason === 'OTHER') {
      // Both custom reason and blockReason match original, hide auth section
      setShowAuthSection(false);
    }
    // If auth section is already shown, keep it visible (don't hide when switching between different reasons)
  };

  const handleBlockReasonConfirm = () => {
    if (!blockReason) {
      return;
    }
    
    if (blockReason === 'OTHER' && !customBlockReason.trim()) {
      return;
    }

    // Show auth section after reason is selected
    setShowBlockReason(false);
    setShowAuthSection(true);
  };

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setCodeError(false);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    for (let i = 0; i < pastedData.length && i < 4; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex((digit, idx) => !digit && idx < 4);
    if (nextEmptyIndex !== -1) {
      inputRefs[nextEmptyIndex].current?.focus();
    } else {
      inputRefs[3].current?.focus();
    }
  };

  const handleAuthConfirm = async () => {
    const enteredCode = code.join('');
    if (enteredCode.length !== 4) {
      setCodeError(true);
      return;
    }

    if (!task || !pendingStatus) return;

    try {
      setLoading(true);

      // Get the block reason value (enum) for API
      let finalBlockReason = null;
      if (pendingStatus.key === 'BLOCKED' && blockReason) {
        if (blockReason === 'OTHER') {
          // Send the exact text entered by the user for "OTHER"
          finalBlockReason = customBlockReason;
        } else {
          // Use the enum value from BLOCK_REASONS
          const reasonObj = BLOCK_REASONS.find(r => r.value === blockReason);
          finalBlockReason = reasonObj?.value || blockReason;
        }
      }

      // Prepare payload according to API specification
      const payload = {
        status: pendingStatus.key,
        secretCode: enteredCode,
      };

      // Only include blockedReason if status is BLOCKED and reason is provided
      if (pendingStatus.key === 'BLOCKED' && finalBlockReason) {
        payload.blockedReason = finalBlockReason;
      }

      console.log(payload);
      // Call PUT /projects/tasks/{id}/status
      await securePut(`/projects/tasks/${displayTask.id}/status`, payload, showSnackbar);

      // Refresh task data after status update
      try {
        const response = await secureGet(`/projects/tasks/${displayTask.id}`, showSnackbar);
        setTaskData(response.data);
        if (response.data?.status) {
          setCurrentTaskStatus(response.data.status);
        }
      } catch (error) {
        console.error('Error refreshing task after status update:', error);
        // Update status locally if refresh fails
      setCurrentTaskStatus(pendingStatus.key);
      }

      // Reset states
      resetForm();

      // Call onSuccess callback to refresh tasks in parent
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error updating task status:', error);
      //showSnackbar(error.response.data.statusLabel, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Reset all form values
  const resetForm = () => {
    setShowStatusDropdown(false);
    setShowAuthSection(false);
    setShowBlockReason(false);
    setPendingStatus(null);
    setBlockReason('');
    setCustomBlockReason('');
    setOriginalBlockReason(null);
    setOriginalCustomBlockReason(null);
    setCode(['', '', '', '']);
    setCodeError(false);
    // Reset snackbar state
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAuthCancel = () => {
    resetForm();
    onClose();
  };

  const handleBlockReasonCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
        <div className="bg-[#F9FAFC] rounded-[12px] shadow-lg max-w-[1200px] w-full py-12 px-14 relative">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <img src={closeIcon} alt="Close" className="w-6 h-6" />
          </button>

          {/* Loading State */}
          {fetchingTask && (
            <div className="flex justify-center items-center py-12">
              <LoadingLogo />
            </div>
          )}

          {/* Content - Only show when not fetching */}
          {!fetchingTask && displayTask && (
            <>
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-[32px] font-semibold text-[#000000] mb-3">
              {displayTask.taskNumber} • {displayTask.description} • {displayTask.projectNumber}
              {displayTask.parentTaskId && ` • Liée à : ${displayTask.parentTaskNumber}`}
            </h2>
            
            <div className="flex items-center justify-between">
              {/* Technician Badge */}
              <div
                className="inline-block px-3 py-1 rounded-[12px] text-[16px] font-bold text-white"
                style={{ backgroundColor: technician?.color || '#ACAFC6' }}
              >
                 {technician?.name}
              </div>

              {/* Status Dropdown - Aligned to Right */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="px-4 py-2 bg-white text-[14px] font-bold rounded-[12px] flex items-center gap-2 hover:bg-gray-200 transition shadow-[0px_20px_80px_#00000011]"
                  style={{ color: currentStatus.color }}
                >
                  {currentStatus.label}
                  <img 
                    src={showStatusDropdown ? chevronUpIcon : chevronDownIcon} 
                    alt="chevron" 
                    className="w-4 h-4" 
                  />
                </button>

                {/* Status Dropdown Menu */}
                {showStatusDropdown && (
                  <div className="absolute top-full mt-1 right-0 w-[140px] px-2 bg-white rounded-[12px] py-2 z-10 shadow-[0px_20px_80px_#00000011]">
                    {STATUSES.filter(s => s.key !== displayStatus).map((status, index, array) => (
                      <React.Fragment key={status.key}>
                        <button
                          onClick={() => handleStatusSelect(status)}
                          className="w-full px-4 py-2 text-left text-[14px] font-semibold hover:bg-gray-100 transition"
                          style={{ color: status.color }}
                        >
                          {status.label}
                        </button>
                        {index < array.length - 1 && (
                          <div className="h-[1px] bg-[#F7F7F7]" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="bg-[#FFFFFF] rounded-[12px] px-8 py-5 mb-6 shadow-[0px_6px_26px_#626B7D12]">
            <div className="grid grid-cols-5 gap-4">
{/*               <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">PROJET</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{displayTask.projectNumber || '—'}</p>
              </div> */}
              <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">N° IMMAT</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{vehicleData?.immatriculationNumber || '—'}</p>
              </div>
              <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">MARQUE</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{vehicleData?.brand?.name || '—'}</p>
              </div>
              <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">MODÈLE</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{vehicleData?.model || '—'}</p>
              </div>
              <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">COULEUR</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{vehicleData?.color || '—'}</p>
              </div>
              <div>
                <p className="text-[14px] text-[#707070] font-semibold mb-1">VIN</p>
                <p className="text-[20px] text-[#38485C] font-semibold">{vehicleData?.vinNumber  || '—'}</p>
              </div>
            </div>
          </div>

          {/* Time Info */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <span className="text-[16px] text-[#707070] font-semibold">ESTIMÉ: </span>
              <span className="text-[16px] text-primary font-bold">
                {formatDuration(displayTask.estimatedDuration)}
              </span>
            </div>
            <div>
              <span className="text-[16px] text-[#707070] font-semibold">ÉCOULÉ: </span>
              <span className="text-[16px] text-primary font-bold">
                {calculateElapsedTime(displayTask.startDate, displayTask.endDate, displayTask.totalBlockedTime, displayTask.totalPausedTime, displayTask.status, displayTask.statusLastChangedAt) || '—'}
              </span>
            </div>
          </div>

                    {/* Block Reason Section */}
                    {showBlockReason && (
            <div className="mb-6">
              {/* Title, Subtitle and Dropdown - All in one row */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-[24px] font-semibold text-[#38485C] mb-2">
                    {t('kanban.select_block_reason')}
                  </h3>
                  <p className="text-[16px] text-[#4D5B68] font-semibold">
                    {t('kanban.choose_option')}
                  </p>
                </div>

                {/* Reason Dropdown - Aligned to Right */}
                <div className="w-[400px]">
                  <Input
                    type="dropdown"
                    label={t('kanban.block_reason')}
                    name="blockReason"
                    value={blockReason || ''}
                    onChange={handleBlockReasonChange}
                    options={BLOCK_REASONS.map(reason => ({
                      value: reason.value,
                      label: reason.name,
                      name: reason.name,
                      id: reason.value // Add id to help with value matching
                    }))}
                    required={true}
                  />
                </div>
              </div>

              {/* Custom Reason Input (only if "Autre" selected) */}
              {blockReason === 'OTHER' && (
                <div className="flex justify-end mb-4">
                  <div className="w-[400px]" ref={customReasonInputRef}>
                    <Input
                      type="text"
                      label={t('kanban.specify_reason')}
                      name="customBlockReason"
                      value={customBlockReason}
                      onChange={handleCustomBlockReasonChange}
                      required={true}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inline Authentication Section */}
          {showAuthSection && (
            <div className="mb-6">
              {/* Title, Instructions and Code Input - All in one row */}
              <div className="flex items-center justify-between">
                <div>
                  {/* Title */}
                  <h3 className="text-[24px] font-semibold text-[#38485C] mb-2">
                    {t('kanban.auth_required')}
                  </h3>
                  {/* Instructions */}
                  <p className="text-[16px] font-semibold text-[#4D5B68]">
                    {t('kanban.auth_instructions')}
                  </p>
                </div>

                {/* Code Input Fields */}
                <div className="flex gap-3">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={inputRefs[index]}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      onPaste={index === 0 ? handleCodePaste : undefined}
                      className={`w-14 h-14 text-center text-[#38485C] text-[24px] font-semibold border rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary ${
                        codeError ? 'border-[#EF466F]' : 'border-[#E3E3E3]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

              {/* Submit/Cancel Buttons */}
              <div className="flex justify-end mt-6">
                <SaveCancel
                  onCancelClick={handleAuthCancel}
                  onSaveClick={handleAuthConfirm}
                />
              </div>
            </>
          )}
        </div>
        
        {/* Snackbar Notification */}
        <SnackbarNotification
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          manual={snackbar.manual}
        />
    </div>
  );
};

export default TaskDetailPopup;

